import React from 'react';
import L from 'leaflet';

const CLUSTER_TRANSITION_DURATION = 500;
const THRESHOLD_DISTANCE = 1000;
const SELECTED_FOLLOW_EPSILON = 0.000001;

// Devices in the same fleet report at very different cadences (measured on
// prod: 1s, 3s, 5s and 10s vehicles side by side). Stretching every segment
// over a fixed 10s made a 1s device crawl at a tenth of its real speed, so the
// playout duration follows the gap actually observed per device instead.
const MIN_SEGMENT_DURATION = 700;
const MAX_SEGMENT_DURATION = 12000;
const DEFAULT_SEGMENT_DURATION = 3000;
const MAX_PLAUSIBLE_FIX_GAP = 60000;
// A marker parked on its newest fix (just left a cluster, or was just
// selected) has nothing left to play, so it used to sit frozen until the next
// packet — up to 10s. Replaying the last segment starts it moving at once at
// the cost of trailing live by one packet; cap how far back that may step.
const MAX_REPLAY_REWIND_METERS = 150;

const getFollowCenterLatLng = (map, position, offsetY = 0, zoom = map.getZoom()) => {
  if (!map || !position) return null;

  const latLng = L.latLng(position[0], position[1]);
  const markerPoint = map.project(latLng, zoom);
  const nextCenterPoint = markerPoint.subtract([0, offsetY]);

  return map.unproject(nextCenterPoint, zoom);
};

const AnimationManager = ({
  listVehicleTracking,
  mapRef,
  bounds,
  clusters,
  supercluster,
  vehicleSelected,
  vehiclePositionsRef,
  lastKnownPositionsRef,
  clusterPositionsRef,
  markerRef,
  vehicleIconsRef,
  animationRef,
  lastUpdateRef,
  isPageVisible,
  selectedVehicleFollowOffsetY = 0
}) => {
  const lastTargetPosRef = React.useRef({});
  const lastClusterStateRef = React.useRef({});
  const lastSelectedRef = React.useRef({});
  const lastPageVisibleRef = React.useRef(isPageVisible);
  // deviceId -> { prev, last, createdAt, arrivedAt, gapMs } playout timeline
  const fixTimelineRef = React.useRef({});

  // Unmount cleanup
  React.useEffect(() => {
    return () => {
      Object.values(animationRef.current).forEach(cancelAnimationFrame);
    };
  }, []);

  React.useEffect(() => {
    if (!listVehicleTracking?.length || !mapRef) return;

    clusterPositionsRef.current = {};
    clusters.forEach(cluster => {
      if (!cluster.properties.cluster) return;
      const pos = [cluster.geometry.coordinates[1], cluster.geometry.coordinates[0]];
      const leaves = supercluster.getLeaves(cluster.properties.cluster_id, Infinity);
      leaves.forEach(leaf => {
        const id = leaf.properties.vehicle.device_id;
        if (vehiclePositionsRef.current[id]) {
          lastKnownPositionsRef.current[id] = vehiclePositionsRef.current[id];
        }
        clusterPositionsRef.current[id] = pos;
      });
    });

    const pageVisibleChanged = lastPageVisibleRef.current !== isPageVisible;
    lastPageVisibleRef.current = isPageVisible;

    const currentMapBounds = mapRef.getBounds().pad(0.5);

    const keepSelectedCentered = position => {
      const center = mapRef.getCenter();
      const followCenter = getFollowCenterLatLng(
        mapRef,
        position,
        selectedVehicleFollowOffsetY
      );
      if (!followCenter) return;
      if (
        Math.abs(center.lat - followCenter.lat) > SELECTED_FOLLOW_EPSILON ||
        Math.abs(center.lng - followCenter.lng) > SELECTED_FOLLOW_EPSILON
      ) {
        mapRef.setView(followCenter, mapRef.getZoom(), { animate: false });
      }
    };

    const playSegment = ({ deviceId, from, to, duration, isSelected }) => {
      if (animationRef.current[deviceId]) {
        cancelAnimationFrame(animationRef.current[deviceId]);
      }

      const startTime = performance.now();
      lastUpdateRef.current[deviceId] = startTime;

      const animate = now => {
        const progress =
          duration > 0 ? Math.min((now - startTime) / duration, 1) : 1;
        const lat = from[0] + (to[0] - from[0]) * progress;
        const lng = from[1] + (to[1] - from[1]) * progress;

        vehiclePositionsRef.current[deviceId] = [lat, lng];

        const inView = mapRef.getBounds().pad(0.5).contains([lat, lng]);
        if (inView || isSelected) {
          markerRef.current[deviceId]?.setLatLng([lat, lng]);
        }
        if (isSelected) {
          keepSelectedCentered([lat, lng]);
        }

        if (progress < 1) {
          animationRef.current[deviceId] = requestAnimationFrame(animate);
        } else {
          vehiclePositionsRef.current[deviceId] = to;
          animationRef.current[deviceId] = null;
        }
      };

      animationRef.current[deviceId] = requestAnimationFrame(animate);
    };

    listVehicleTracking.forEach(vehicle => {
      if (vehicle.debt) return;

      const deviceId = vehicle.device_id;
      const nextLat = Number(vehicle.lat) || 0;
      const nextLng = Number(vehicle.lng) || 0;
      // Giữ đúng mảng cũ khi toạ độ không đổi: mảng mới làm vỡ memo của MarkerManager.
      const knownTarget = lastTargetPosRef.current[deviceId];
      const newPos =
        knownTarget && knownTarget[0] === nextLat && knownTarget[1] === nextLng
          ? knownTarget
          : [nextLat, nextLng];
      const isInViewport = currentMapBounds.contains(newPos);
      const isSelected = vehicleSelected.device_id === deviceId;
      const isInCluster = !!clusterPositionsRef.current[deviceId];

      const prevTarget = lastTargetPosRef.current[deviceId];
      const prevCluster = lastClusterStateRef.current[deviceId];
      const prevSelected = lastSelectedRef.current[deviceId];

      const targetChanged = !prevTarget || prevTarget[0] !== newPos[0] || prevTarget[1] !== newPos[1];
      const clusterChanged = prevCluster !== isInCluster;
      const selectedChanged = prevSelected !== isSelected;

      // Update tracking refs
      lastTargetPosRef.current[deviceId] = newPos;
      lastClusterStateRef.current[deviceId] = isInCluster;
      lastSelectedRef.current[deviceId] = isSelected;

      if (targetChanged) {
        const previousFix = fixTimelineRef.current[deviceId];
        const arrivedAt = performance.now();
        const createdAt = Number(vehicle.created_at) || 0;

        // Prefer the device clock; fall back to arrival time when the device
        // timestamps are missing, out of order or absurdly far apart.
        let gapMs = 0;
        if (previousFix) {
          const byDeviceClock = (createdAt - previousFix.createdAt) * 1000;
          gapMs =
            byDeviceClock > 0 && byDeviceClock < MAX_PLAUSIBLE_FIX_GAP
              ? byDeviceClock
              : arrivedAt - previousFix.arrivedAt;
        }

        fixTimelineRef.current[deviceId] = {
          prev: previousFix ? previousFix.last : null,
          last: newPos,
          createdAt,
          arrivedAt,
          gapMs: gapMs
            ? Math.min(
                MAX_SEGMENT_DURATION,
                Math.max(MIN_SEGMENT_DURATION, gapMs)
              )
            : previousFix?.gapMs || DEFAULT_SEGMENT_DURATION
        };
      }

      const fix = fixTimelineRef.current[deviceId];
      const segmentDuration = fix?.gapMs || DEFAULT_SEGMENT_DURATION;

      if (!isInViewport && !isSelected) {
        if (animationRef.current[deviceId]) {
          cancelAnimationFrame(animationRef.current[deviceId]);
          animationRef.current[deviceId] = null;
        }
        vehiclePositionsRef.current[deviceId] = newPos;
        return;
      }

      if (!targetChanged && !clusterChanged && !selectedChanged && !pageVisibleChanged && animationRef.current[deviceId]) {
        return;
      }

      const wasInCluster = !!clusterPositionsRef.current[deviceId];
      const oldPos = vehiclePositionsRef.current[deviceId] || newPos;
      const lastKnown = lastKnownPositionsRef.current[deviceId] || oldPos;

      if (isInCluster) {
        if (animationRef.current[deviceId]) {
          cancelAnimationFrame(animationRef.current[deviceId]);
          animationRef.current[deviceId] = null;
        }
        vehiclePositionsRef.current[deviceId] = newPos;
        return;
      }

      const samePos = oldPos[0] === newPos[0] && oldPos[1] === newPos[1];
      if (samePos && !wasInCluster && !isInCluster) {
        const elapsed = fix ? performance.now() - fix.arrivedAt : Infinity;
        const canReplay =
          !!fix?.prev &&
          (fix.prev[0] !== newPos[0] || fix.prev[1] !== newPos[1]) &&
          elapsed >= 0 &&
          elapsed < segmentDuration;

        if (!canReplay) {
          vehiclePositionsRef.current[deviceId] = newPos;
          markerRef.current[deviceId]?.setLatLng(newPos);
          return;
        }

        // Resume the playout timeline: walk the last segment from where it
        // should be right now instead of leaving the marker parked on the
        // newest fix. Bound how far back that steps so a 10s device does not
        // visibly jump backwards.
        const segmentMeters = L.latLng(fix.prev[0], fix.prev[1]).distanceTo(
          L.latLng(newPos[0], newPos[1])
        );
        const progressFloor =
          segmentMeters > MAX_REPLAY_REWIND_METERS
            ? 1 - MAX_REPLAY_REWIND_METERS / segmentMeters
            : 0;
        const progress = Math.max(elapsed / segmentDuration, progressFloor);

        playSegment({
          deviceId,
          from: [
            fix.prev[0] + (newPos[0] - fix.prev[0]) * progress,
            fix.prev[1] + (newPos[1] - fix.prev[1]) * progress
          ],
          to: newPos,
          duration: segmentDuration - elapsed,
          isSelected
        });
        return;
      }

      const sameAsLast = lastKnown[0] === newPos[0] && lastKnown[1] === newPos[1];
      if (wasInCluster && !isInCluster && sameAsLast) {
        vehiclePositionsRef.current[deviceId] = newPos;
        markerRef.current[deviceId]?.setLatLng(newPos);
        return;
      }

      const newIcon = window.handleDisplayIcon?.(vehicle);
      if (newIcon && markerRef.current[deviceId]) {
        const current = vehicleIconsRef.current[deviceId];
        if (!current || current.options.iconUrl !== newIcon.options.iconUrl) {
          markerRef.current[deviceId].setIcon(newIcon);
          vehicleIconsRef.current[deviceId] = newIcon;
        }
      }

      const oldLatLng = L.latLng(oldPos[0], oldPos[1]);
      const newLatLng = L.latLng(newPos[0], newPos[1]);
      const distance = oldLatLng.distanceTo(newLatLng);

      if (!isPageVisible || distance > THRESHOLD_DISTANCE) {
        vehiclePositionsRef.current[deviceId] = newPos;
        if ((isInViewport || isSelected)) {
          markerRef.current[deviceId]?.setLatLng(newPos);
        }
        if (isSelected) {
          keepSelectedCentered(newPos);
        }
      } else {
        const startPos = wasInCluster ? lastKnown : oldPos;
        // Play the segment over the gap this device actually reports at, so the
        // marker travels at the vehicle's real speed and lands on the newest
        // fix just as the next packet arrives. When this run was triggered by
        // something other than a new fix (selection, clustering, tab focus —
        // selecting also cancels every in-flight frame) only the time left in
        // the current segment remains, otherwise the marker would restart a
        // full-length crawl over the leftover distance.
        const elapsedInSegment = fix ? performance.now() - fix.arrivedAt : 0;
        const duration =
          wasInCluster && !isInCluster
            ? CLUSTER_TRANSITION_DURATION
            : Math.max(segmentDuration - elapsedInSegment, 0);

        playSegment({
          deviceId,
          from: startPos,
          to: newPos,
          duration,
          isSelected
        });
      }
    });
  }, [
    listVehicleTracking,
    mapRef,
    clusters,
    supercluster,
    vehicleSelected,
    isPageVisible,
    selectedVehicleFollowOffsetY
  ]);

  return null;
};

export default AnimationManager;
